import { Box, Button } from "@mui/material";
import useColors from "../../api/hooks/useColors";
import SvgToIcon from "../../icons/SvgToIcon";
import LoadingIcon from "../LoadingIcon";

// TODO: Add this to our documentations
export default function SoftButton({
  onClick,
  color = "primary",
  size = "small",
  icon = null,
  disabledIcon = null,
  disabled = false,
  loading = false,
  children = null,
  sx = {},
  ...props
}) {
  /*
  If you use a currentColor .svg, the enabled/disabled colors will be the same as the text color.
  If you use a colored .svg (for example, a company's logo like Jira), you need to provide a disabledIcon .svg.
  If you provide only an icon (no text), the button will be square.
  */
  const currentColorMode = !disabledIcon;
  const colors = useColors();

  const colorMap = {
    primary: {
      enabled: {
        text: colors.button_soft_primary_enabled_text,
        bg: colors.button_soft_primary_enabled_bg,
        icon: colors.button_soft_primary_enabled_icon,
      },
      hover: {
        text: colors.button_soft_primary_hovered_text,
        bg: colors.button_soft_primary_hovered_bg,
        icon: colors.button_soft_primary_hovered_icon,
      },
      disabled: {
        text: colors.button_soft_disabled_text,
        bg: colors.button_soft_disabled_bg,
        icon: colors.button_soft_disabled_icon,
      },
    },
  };

  const sizeMap = {
    xs: {
      fontSize: "12px",
      fontWeight: 400,
      height: "24px",
      minWidth: "min-content",
      paddingX: "10px",
      borderRadius: "10px",
      loadingSize: 10,
      iconSize: 14,
    },
    small: {
      fontSize: "12px",
      fontWeight: 500,
      height: "32px",
      minWidth: "min-content",
      paddingX: "10px",
      borderRadius: "10px",
      loadingSize: 14,
      iconSize: 18,
    },
    medium: {
      fontSize: "14px",
      fontWeight: 500,
      height: "36px",
      minWidth: "min-content",
      paddingX: "10px",
      borderRadius: "12px",
      loadingSize: 16,
      iconSize: 20,
    },
    large: {
      fontSize: "14px",
      fontWeight: 500,
      height: "48px",
      minWidth: "min-content",
      paddingX: "10px",
      borderRadius: "14px",
      loadingSize: 18,
      iconSize: 20,
    },
  };

  const colorConfig = colorMap[color];
  const sizeConfig = sizeMap[size];
  const width = !children && icon ? sizeConfig.height : "fit-content";

  return (
    <Button
      variant="text"
      onClick={onClick}
      disabled={disabled || loading}
      disableRipple
      disableFocusRipple
      disableElevation
      sx={{
        position: "relative",
        textTransform: "none",
        fontSize: sizeConfig.fontSize,
        fontWeight: sizeConfig.fontWeight,
        height: sizeConfig.height,
        paddingX: sizeConfig.paddingX,
        width: width,
        minWidth: children ? sizeConfig.minWidth : width,
        borderRadius: sizeConfig.borderRadius,
        color: colorConfig.enabled.text,
        backgroundColor: colorConfig.enabled.bg,
        "&:hover": {
          backgroundColor: colorConfig.hover.bg,
          color: colorConfig.hover.text,
        },
        "&:disabled": {
          backgroundColor: colorConfig.disabled.bg,
          color: colorConfig.disabled.text,
        },
        ...sx,
      }}
      {...props}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={0.7}
        width="100%"
        justifyContent="center"
        whiteSpace="nowrap"
        style={{ visibility: loading ? "hidden" : "visible" }}
      >
        {icon && (
          <Box display="flex" alignItems="center">
            <SvgToIcon
              IconLogo={
                currentColorMode ? icon : !disabled ? icon : disabledIcon
              }
              width={sizeConfig.iconSize}
              height={sizeConfig.iconSize}
              style={
                currentColorMode
                  ? {
                      color: !disabled
                        ? colorConfig.enabled.icon
                        : colorConfig.disabled.icon,
                    }
                  : {}
              }
            />
          </Box>
        )}
        {children && children}
      </Box>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadingIcon type="circular" size={sizeConfig.loadingSize} />
        </Box>
      )}
    </Button>
  );
}
